import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { GoFileDirectory } from 'react-icons/go';
import { getResults } from './metaAnalysisApi';
import { listProfileJobs } from './profileApi';
import { getDataSourceById, toUiDataSource } from '../DataSources/datasourcesApi';
import { formatBytes, displaySniffType } from 'Utils/fileTypeUtils';
import FileTypeChip from '../DataSources/file/FileTypeChip';
import FolderDiscoverDialog from './FolderDiscoverDialog';
import {
  parentDir,
  baseName,
  decodeFolderParam,
  normalizeFolderKey,
} from './folderPathUtils';
import { INK, STATUS, ELEVATION, RADIUS } from 'Utils/styleTokens';
import './styles.css';

// Selection soft cap — an anchored alternation of more names than this makes an
// unwieldy regex; the whole-folder action covers the "everything" case anyway.
const MAX_SELECTED_FILES = 500;

/**
 * Folder drill-down page (/metadata/source/:dataSourceId/folder/:folderPath+).
 * Shows one profiled folder's path + file list and starts discovery of either the
 * whole folder (with a "discover sub folders" option) or a checkbox-selected subset
 * of files — both by creating a NEW data source scoped to this folder (see
 * FolderDiscoverDialog). Refresh-safe: state from the navigation is only a fast
 * first paint; the real load re-derives everything from the URL.
 */
const FolderDetail = () => {
  const { dataSourceId, folderPath: folderParam } = useParams();
  const history = useHistory();
  const location = useLocation();

  const decodedFolder = decodeFolderParam(folderParam);
  const navState = location.state || {};

  const [source, setSource] = useState(null);
  const [files, setFiles] = useState(Array.isArray(navState.files) ? navState.files : []);
  // Display path keeps its original slashes; recovered from nav state or the first
  // matched file once results load. Cloud-scheme folders (s3://…, arriving from the URL
  // as 's3:/…') already carry their own root, so only bare paths get a leading slash.
  const [displayPath, setDisplayPath] = useState(
    navState.folderPath || (decodedFolder.includes(':/') ? `${decodedFolder}/` : `/${decodedFolder}/`)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [recurse, setRecurse] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'folder' | 'selection'

  const load = useCallback(async () => {
    let cancelled = false;
    try {
      const [dto, jobsPage] = await Promise.all([
        getDataSourceById(dataSourceId),
        listProfileJobs({ dataSourceId }),
      ]);
      if (cancelled) return () => { cancelled = true; };
      setSource(toUiDataSource(dto));
      const jobs = (jobsPage && jobsPage.content) || (Array.isArray(jobsPage) ? jobsPage : []);
      const succeeded = jobs.find((j) => j.status === 'SUCCEEDED');
      if (succeeded) {
        const res = await getResults(succeeded.id);
        const payload = res && res.payload ? res.payload : res;
        const fs = payload && payload.fileScan ? payload.fileScan : null;
        const all = fs && Array.isArray(fs.files) ? fs.files : [];
        const wanted = normalizeFolderKey(decodedFolder);
        const inFolder = all.filter(
          (f) => normalizeFolderKey(parentDir(f.path || f.name)) === wanted
        );
        if (!cancelled) {
          setFiles(inFolder);
          if (inFolder.length > 0) setDisplayPath(parentDir(inFolder[0].path || inFolder[0].name));
        }
      }
      if (!cancelled) setError(null);
    } catch (e) {
      if (!cancelled) setError((e && e.message) || 'Failed to load folder detail');
    }
    return () => { cancelled = true; };
  }, [dataSourceId, decodedFolder]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => baseName(a).localeCompare(baseName(b))),
    [files]
  );
  const totalBytes = useMemo(
    () => files.reduce((s, f) => s + (f.sizeBytes || 0), 0),
    [files]
  );

  const keyOf = (f) => f.path || f.name;
  const allSelected = files.length > 0 && selected.size === files.length;

  const toggleFile = (f) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = keyOf(f);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(files.map(keyOf)));
  };

  const selectedFiles = useMemo(
    () => files.filter((f) => selected.has(keyOf(f))),
    [files, selected]
  );

  const overCap = selectedFiles.length > MAX_SELECTED_FILES;

  return (
    <div className='metadata-page'>
      <div className='metadata-toolbar'>
        <span className='mt-title'>FOLDER DETAIL</span>
        <span className='mt-sep'>|</span>
        <button
          type='button'
          className='metadata-sortbtn'
          onClick={() => history.push('/metadata/source/' + encodeURIComponent(dataSourceId))}
        >
          ← Back to data source
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
        {loading && files.length === 0 ? (
          <div>
            <Skeleton height={28} width={420} />
            <div style={{ marginTop: 12 }}><Skeleton height={48} /></div>
            <div style={{ marginTop: 12 }}><Skeleton height={240} /></div>
          </div>
        ) : error && files.length === 0 ? (
          <div className='metadata-empty' style={{ color: STATUS.danger.text }}>{error}</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GoFileDirectory size={20} style={{ opacity: 0.7, flexShrink: 0 }} />
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {displayPath}
              </h2>
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: INK[500] }}>
              {source && source.name ? <>Data source <strong>{source.name}</strong> · </> : null}
              {files.length} {files.length === 1 ? 'file' : 'files'} · {formatBytes(totalBytes)}
            </div>

            {/* Action bar: whole-folder discovery (with recurse) or selected subset. */}
            <div style={{
              marginTop: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              padding: '12px 16px', border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md,
              background: '#fff', boxShadow: ELEVATION.e1,
            }}>
              <Form.Check
                type='checkbox'
                id='folder-detail-recurse'
                data-testid='folder-detail-recurse'
                label='Discover sub folders'
                checked={recurse}
                onChange={(e) => setRecurse(e.target.checked)}
                style={{ fontSize: 13 }}
              />
              <Button
                size='sm'
                variant='primary'
                data-testid='discover-folder-btn'
                onClick={() => setDialog('folder')}
                disabled={!source}
              >
                Discover entire folder…
              </Button>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                {overCap && (
                  <span style={{ fontSize: 12, color: STATUS.danger.text }}>
                    Select at most {MAX_SELECTED_FILES} files — use "entire folder" beyond that.
                  </span>
                )}
                <Button
                  size='sm'
                  variant='outline-primary'
                  data-testid='discover-selected-btn'
                  onClick={() => setDialog('selection')}
                  disabled={!source || selectedFiles.length === 0 || overCap}
                >
                  Discover {selectedFiles.length > 0 ? `${selectedFiles.length} selected file${selectedFiles.length === 1 ? '' : 's'}` : 'selected files'}…
                </Button>
              </div>
            </div>

            {files.length === 0 ? (
              <div className='metadata-empty' style={{ padding: 24, marginTop: 16 }}>
                No profiled files found for this folder. Run a profile on the data source first.
              </div>
            ) : (
              <div style={{ marginTop: 16, border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md, overflow: 'auto' }}>
                <table className='metadata-itbl'>
                  <thead>
                    <tr>
                      <th style={{ width: 34, textAlign: 'center' }}>
                        <input
                          type='checkbox'
                          aria-label='Select all files'
                          data-testid='select-all-files'
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = selected.size > 0 && !allSelected;
                          }}
                          onChange={toggleAll}
                        />
                      </th>
                      <th style={{ textAlign: 'left', paddingLeft: 10 }}>File</th>
                      <th style={{ textAlign: 'left', paddingLeft: 10 }}>Type</th>
                      <th style={{ textAlign: 'left', paddingLeft: 10 }}>Detected</th>
                      <th>Size</th>
                      <th>Analyzed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFiles.map((f, i) => {
                      const k = keyOf(f);
                      return (
                        <tr
                          key={k || i}
                          onClick={() => toggleFile(f)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type='checkbox'
                              aria-label={`Select ${baseName(f)}`}
                              checked={selected.has(k)}
                              onChange={() => toggleFile(f)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>
                            {baseName(f)}
                            {f.notice && (
                              <div style={{ fontSize: 11, color: STATUS.danger.text }}>{f.notice}</div>
                            )}
                          </td>
                          <td style={{ textAlign: 'left', paddingLeft: 10 }}>
                            {f.type ? <FileTypeChip type={f.type} /> : '—'}
                          </td>
                          <td style={{ textAlign: 'left', paddingLeft: 10 }}>
                            {f.sniffedType ? (() => {
                              // Mismatch = extension contradicts content; declared UNKNOWN
                              // (no extension) is an identification, not a contradiction.
                              const mismatch = f.type && f.type !== 'UNKNOWN' && f.sniffedType !== f.type;
                              const shown = displaySniffType(f.type, f.sniffedType);
                              return (
                                <span
                                  title={mismatch
                                    ? `Content is ${f.sniffedType} but the file name says ${f.type} — the detected type is used for analysis`
                                    : shown === 'TEXT'
                                      ? 'File type not recognized from the name — content is readable text'
                                      : (!f.type || f.type === 'UNKNOWN')
                                        ? 'File type not recognized from the name — identified from content'
                                        : 'Content matches the file extension'}
                                  style={mismatch ? {
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '1px 4px', borderRadius: 4,
                                    border: '1px solid rgba(180,83,9,0.45)', background: 'rgba(180,83,9,0.06)',
                                  } : undefined}
                                >
                                  {mismatch && (
                                    <span style={{ fontSize: 11, color: STATUS.warning.text }}>⚠</span>
                                  )}
                                  <FileTypeChip type={shown} />
                                </span>
                              );
                            })() : '—'}
                          </td>
                          <td style={{ color: INK[500] }}>{formatBytes(f.sizeBytes)}</td>
                          <td style={{ color: INK[500] }}>{f.analyzed ? '✓' : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <FolderDiscoverDialog
              show={dialog != null}
              onHide={() => setDialog(null)}
              mode={dialog || 'folder'}
              folderPath={displayPath}
              recurse={recurse}
              selectedFiles={selectedFiles}
              folderFiles={files}
              parentSource={source}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FolderDetail;
